import { css } from '@emotion/react'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import { type ReactElement, type ReactNode, cloneElement, useMemo } from 'react'
import ClickWrapper from '../ClickWrapper'

export interface SidebarTabProps extends Omit<HTMLMotionProps<'div'>, 'title'> {
	icon: ReactElement
	title: ReactNode
	isActive?: boolean
}

// 处理活动状态背景的子组件
function ActiveBackground() {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			css={css`
				background-color: var(--bg-on-body-2);
				position: absolute;
				inset: 0;
				z-index: 0;
				height: 100%;
				width: 100%;
				border-radius: inherit;
			`}
		/>
	)
}

// 处理图标的子组件
function TabIcon({ icon }: { icon: ReactElement }) {
	return (
		<div
			css={css`
				position: relative;
				z-index: 1;
			`}
		>
			{cloneElement(icon, {
				...icon.props,
				width: '28px',
				height: '28px',
				display: 'block',
			})}
		</div>
	)
}

export default function SidebarTab({
	id,
	icon,
	title,
	isActive = false,
	...props
}: SidebarTabProps) {
	const tabStyles = useMemo(
		() => [
			css`
				display: flex;
				align-items: center;
				justify-content: center;
				gap: 12px;
				padding: 8px;
				border-radius: 10px;
				cursor: pointer;
				user-select: none;
				color: var(--tint-primary);
				font-size: 20px;
				line-height: 28px;
				letter-spacing: 0.25px;
				outline: 1.5px solid transparent;
				transition: outline-color 0.3s;
				position: relative;

				&:hover {
					outline-color: var(--bg-on-body-2);
				}
			`,
			isActive &&
				css`
					font-weight: 500;
				`,
		],
		[isActive]
	)

	return (
		<ClickWrapper whileTap={{ scale: 0.98 }} css={tabStyles} {...props}>
			<AnimatePresence>{isActive && <ActiveBackground />}</AnimatePresence>
			<TabIcon icon={icon} />
		</ClickWrapper>
	)
}
